'use client';
import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { avatarImg } from '@/lib/imgUrl';
import { getZoomNatural, setZoomNatural, zoomPxFor } from '@/lib/avatarZoomSize';

// Same zoom engine/settings as ReviewerAvatar (reviewer-image popup) — semi-transparent
// black backdrop, circular crop, white ring + shadow — just wrapping an arbitrary trigger
// element (e.g. the pro's own profile picture at whatever size it's rendered at) instead of
// owning a fixed-size avatar itself.
export default function AvatarZoom({ src, name, children }: { src: string; name: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  // FINAL zoom size, known BEFORE the photo is shown (15/10/26: it used to open at 280 and shrink to 120 after load). null = natural size not known yet:
  // the photo then stays invisible (opacity 0, same box) until it has loaded, and appears already at its final size.
  const [zoomPx, setZoomPx] = useState<number | null>(null);
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [open]);

  // pointerdown starts the exact zoom fetch (same avatarImg(src,280) candidate IdlePrefetch warms) ~100ms before the click lands
  const probe = () => {
    const a = avatarImg(src, 280);
    if (getZoomNatural(a.src)) return;
    const el = new Image(); if (a.srcSet) el.srcset = a.srcSet; el.src = a.src;
    el.onload = () => setZoomNatural(a.src, el.naturalWidth);
  };
  const openZoom = () => {
    const nw = getZoomNatural(avatarImg(src, 280).src);
    setZoomPx(nw ? zoomPxFor(nw) : null);
    setOpen(true);
  };

  return (
    <>
      <button onClick={openZoom} onPointerDown={probe} type="button"
        style={{ display: 'block', padding: 0, border: 'none', background: 'transparent', cursor: 'zoom-in' }}>
        {children}
      </button>
      {open && mounted && createPortal(
        <div onClick={() => setOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,.85)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: 24, cursor: 'zoom-out' }}>
          <img {...avatarImg(src, 280)} alt={name} ref={el => { if (el && el.complete && el.naturalWidth) setZoomPx(p => p ?? zoomPxFor(el.naturalWidth)); }} referrerPolicy="no-referrer" onClick={e => e.stopPropagation()}
            onLoad={e => { const w = e.currentTarget.naturalWidth; setZoomNatural(avatarImg(src, 280).src, w); setZoomPx(zoomPxFor(w || 187)); }}
            onError={() => setZoomPx(p => p ?? 280)}
            style={{ width: `min(${zoomPx ?? 280}px, 78vw)`, height: `min(${zoomPx ?? 280}px, 78vw)`, opacity: zoomPx == null ? 0 : 1, flexShrink: 0, borderRadius: '50%', objectFit: 'cover', border: '4px solid rgba(255,255,255,.9)', boxShadow: '0 10px 50px rgba(0,0,0,.5)' }} />
          <div style={{ color: '#fff', fontSize: 17, fontWeight: 700 }}>{name}</div>
        </div>,
        document.body
      )}
    </>
  );
}
